/* jscs:disable maximumLineLength */
import ReactGA from 'react-ga';
import ReactGA4 from 'react-ga4';
import isEmpty from 'lodash/isEmpty';
import { getCurrentPath } from '../../core/util/url';
import UrgentCareForKidsBingPixel from '../../components/Tracking/UrgentCareForKidsBing';
import MetroUrgentCareAdWords from '../../components/Tracking/MetroUrgentCareAdWords';
import { isDev } from '../../config/index';

export enum GAEventType {
  GA_EVENT = 'event',
  GA_PAGEVIEW = 'pageview',
  GA_INITIALIZE_OR_PAGEVIEW = 'initialize_or_pageview',
}

export enum GAEventAction {
  BOOKING = 'book',
  STATUS_TO_CANCELLED = 'change',
  EXTERNAL_BOOKING = 'external-booking-flow',
  SCHEDULER_LOADED = 'solv-scheduler-loaded',
  MULTILOCATION_LOADED = 'solv-multilocation-page-loaded',
  CDP_LOADED = 'solv-cdp-loaded',
}

export enum GAEventCategory {
  BOOKING = 'booking',
  WORKFLOW = 'workflow',
  EXTERNAL_BOOKING = 'external-booking',
}

type TrackingPropertiesGA4 = {
  isGA4: true;
  trackerId: string;
};

type TrackingPropertiesUA = {
  isGA4: false;
  trackerId: string;
  trackerName: string;
  forceSsl: boolean;
  referringDomain: string;
};

interface GATrackingEvent {
  eventAction: GAEventAction;
  eventCategory: string;
  eventType: string;
  eventLabel: string;
}

/**
 * Right now, this is supporting a boolean property in the trackingProperties parameter for isGA4,
 * and based on that will track the events for GA4 or Universal Analytics.
 * UA is being deprecated July 1, 2023, so we can probably rip out all UA code after that point.
 */
const fireGaTracking = ({
  trackingProperties,
  trackingEvent,
}: {
  trackingProperties: TrackingPropertiesGA4 | TrackingPropertiesUA;
  trackingEvent: GATrackingEvent;
}) => {
  const { eventAction, eventCategory, eventType, eventLabel } = trackingEvent;
  if (eventType === GAEventType.GA_EVENT && (!eventAction || !eventCategory)) {
    throw new Error('Need to pass both eventAction and eventCategory for event tracking');
  }
  if (trackingProperties.isGA4) {
    const { trackerId } = trackingProperties;
    if (window) {
      /**
       * This property is set to true in the callback of ReactGA4.event in order to avoid sending automatic pageviews
       * and other automatic events after GA4 has been initialized. Set to false here before sending the event so it
       * makes it through to the partner's GA4 account
       */
      // @ts-ignore
      window[`ga-disable-${trackerId}`] = false;
    }
    ReactGA4.initialize(trackerId, { gtagOptions: { send_page_view: false, groups: 'partner' } });
    if (eventType === GAEventType.GA_EVENT) {
      /**
       * ReactGA4's .event automatically title cases the event action if you use call it with ReactGA.event(options),
       * using ReactGA.event(name, params) does not. All of our events are historically not title-cased,
       * so we use the latter approach.
       */
      ReactGA4.event(eventAction, {
        event_category: eventCategory,
        event_label: eventLabel,
        send_to: 'partner',
        event_callback: (containerId: string) => {
          if (containerId.startsWith(trackerId)) {
            // @ts-ignore
            window[`ga-disable-${trackerId}`] = true;
          }
        },
      });
    }
  } else {
    const { trackerName, trackerId, forceSsl, referringDomain } = trackingProperties;
    const gaOptions = {
      allowLinker: true,
      alwaysSendReferrer: true,
      cookieDomain: 'auto',
      name: trackerName,
    };
    ReactGA.initialize(trackerId, { gaOptions });
    if (forceSsl) {
      ReactGA.ga(`${trackerName}.set`, 'forceSSL', true);
    }
    ReactGA.ga(`${trackerName}.require`, 'linker');
    if (referringDomain) {
      ReactGA.ga(`${trackerName}.linker:autoLink`, [referringDomain]);
    }
    if (eventType === GAEventType.GA_PAGEVIEW) {
      ReactGA.ga(`${trackerName}.send`, eventType, getCurrentPath());
    } else if (eventType === GAEventType.GA_EVENT) {
      ReactGA.ga(`${trackerName}.send`, eventType, eventCategory, eventAction, eventLabel);
    }
  }
};

const getAdWordsTrackingProperties = (location: any) =>
  location?.trackingProperties?.google_adwords ?? location?.tracking_properties?.google_adwords;

const isConfiguredForAdWordsTracking = (location: any) => {
  const adWordsTrackingProperties = getAdWordsTrackingProperties(location);
  return !!(adWordsTrackingProperties?.tracking_id && adWordsTrackingProperties?.conversion_label);
};

const isConfiguredForGoogleAnalyticsTracking = (location: any) => {
  if (!isEmpty(location.trackingProperties)) {
    let isConfiguredForGA4Tracking = false;
    if (!isEmpty(location.trackingProperties.google_analytics4)) {
      isConfiguredForGA4Tracking = !!location.trackingProperties.google_analytics4.id;
    }
    const isConfiguredForUATracking =
      !!location.trackingProperties?.google_analytics?.id &&
      !!location.trackingProperties?.google_analytics?.tracker_name &&
      !!location.trackingProperties?.google_analytics?.referring_domain;

    return isConfiguredForGA4Tracking || isConfiguredForUATracking;
  }
  return false;
};

const fireGoogleAnalytics = (
  location: any,
  eventType = GAEventType.GA_PAGEVIEW,
  eventAction?: GAEventAction,
  eventCategory?: GAEventCategory,
  eventLabel?: string
) => {
  if (isDev()) {
    console.warn('Google Analytics Tracking cannot be fired in dev. exiting.');
    return;
  }

  if (
    isEmpty(location.trackingProperties) ||
    (isEmpty(location.trackingProperties.google_analytics) &&
      isEmpty(location.trackingProperties.google_analytics4))
  ) {
    console.error(
      `Could not fire tracking! Tracking properties for location ${location.id} are not defined`
    );
    return;
  }
  let trackingEvent = { eventType } as GATrackingEvent;
  if (eventType === GAEventType.GA_EVENT) {
    if (!eventAction || !eventCategory || isEmpty(eventAction) || isEmpty(eventCategory)) {
      console.error(
        'Could not fire tracking! Tracking properties eventAction and eventCategory ' +
          'need to be specified for a GA_EVENT'
      );
      return;
    }

    trackingEvent = {
      ...trackingEvent,
      eventAction,
      eventCategory,
      eventLabel: eventLabel || location.id,
    };
  }

  let trackingProperties = {} as TrackingPropertiesGA4 | TrackingPropertiesUA;
  if (!isEmpty(location.trackingProperties.google_analytics)) {
    const uaTrackingProperties = location.trackingProperties.google_analytics;
    const trackingId = uaTrackingProperties.id;
    const trackerName = uaTrackingProperties.tracker_name;
    const referringDomain = uaTrackingProperties.referring_domain;

    if (isEmpty(trackingId) || isEmpty(trackerName) || isEmpty(referringDomain)) {
      console.error(
        'Could not fire UA tracking! trackingId, trackerName, referringDomain must be ' +
          `defined correctly for location ${location.id} `
      );
    } else {
      const forceSsl =
        typeof uaTrackingProperties.force_ssl === 'undefined'
          ? false
          : uaTrackingProperties.force_ssl;

      trackingProperties = {
        trackerId: trackingId,
        trackerName,
        referringDomain,
        forceSsl,
        isGA4: false,
      };

      let uaTrackingEvent = { ...trackingEvent };
      /**
       * we don't track pageviews for GA4 as they are tracked automatically. Instead, we track a specific event
       * on the first pages of the BW, CDP, and Mulitlocation widgets.
       * The original UA implementation tracked individual pageviews so track that instead here
       */
      if (
        (eventAction &&
          [
            GAEventAction.SCHEDULER_LOADED,
            GAEventAction.MULTILOCATION_LOADED,
            GAEventAction.CDP_LOADED,
          ].includes(eventAction)) ||
        eventType === GAEventType.GA_INITIALIZE_OR_PAGEVIEW
      ) {
        uaTrackingEvent.eventType = GAEventType.GA_PAGEVIEW;
      }

      fireGaTracking({
        trackingProperties,
        trackingEvent: uaTrackingEvent,
      });
    }
  }

  if (!isEmpty(location.trackingProperties.google_analytics4)) {
    const ga4TrackingProperties = location.trackingProperties.google_analytics4;
    const trackingId = ga4TrackingProperties.id;
    if (isEmpty(trackingId)) {
      console.error(
        `Could not fire GA4 tracking! trackingId must be defined correctly for location ${location.id}`
      );
      return;
    }
    trackingProperties = {
      trackerId: trackingId,
      isGA4: true,
    };
    fireGaTracking({
      trackingProperties,
      trackingEvent,
    });
  }
};

const getUrgentCareForKidsBingTrackingPixel = () => UrgentCareForKidsBingPixel();
const getMetroUrgentCareAdwordsTracking = () => MetroUrgentCareAdWords();

export {
  getUrgentCareForKidsBingTrackingPixel,
  getMetroUrgentCareAdwordsTracking,
  isConfiguredForGoogleAnalyticsTracking,
  isConfiguredForAdWordsTracking,
  getAdWordsTrackingProperties,
  fireGoogleAnalytics,
};
